/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019-2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */
(function () {
  /**
 * BulbAssetLibrarySnapshotPdfService import google asset as pdf.
 */
  angular.module('bulbApp.assetLibraryModule')
    .constant('SNAPSHOTPDF', {
      USER_INFO_URL: 'https://www.googleapis.com/oauth2/v1/userinfo',
    })
    .service('BulbAssetLibrarySnapshotPdfService', [
      '$q',
      '$translate',
      'BulbAjaxService',
      'BulbAuthService',
      'BulbBusyCoverService',
      'BulbBusyService',
      'BulbErrorService',
      'BulbModalService',
      'BulbPageService',
      'BulbUnitService',
      'Restangular',
      'AUTH',
      'SNAPSHOTPDF',
      'CONFIG',
      'google',
      BulbAssetLibrarySnapshotPdfService,
    ]);

  function BulbAssetLibrarySnapshotPdfService(
    $q,
    $translate,
    BulbAjaxService,
    BulbAuthService,
    BulbBusyCoverService,
    BulbBusyService,
    BulbErrorService,
    BulbModalService,
    BulbPageService,
    BulbUnitService,
    Restangular,
    AUTH,
    SNAPSHOTPDF,
    CONFIG,
    google,
  ) {
    const that = this;
    this.currentUser = BulbAuthService.currentUser;
    const SCOPES = ['https://www.googleapis.com/auth/userinfo.profile', 'https://www.googleapis.com/auth/drive.readonly'];

    /**
   * Store authentication token after authorize and send it to api which be fetched data from google drive.
   * @type {string}
   */
    this.oauthCode = null;

    let googleApiLoaded = false;

    let googleId = null;

    // ==============================
    // REST API call
    // ==============================
    const snapshotPdf = Restangular.all('snapshotPdf');

    // Load the gapi google drive
    if (google.client && google.auth) {
      googleApiLoaded = true;
      // @ts-ignore
    } else if (!google.client && !google.auth && window.gapi) {
      // @ts-ignore
      window.gapi.load('client', () => {
        // @ts-ignore
        google.client = window.gapi.client;
        // @ts-ignore
        window.gapi.load('auth', () => {
          // @ts-ignore
          google.auth = window.gapi.auth;
          googleApiLoaded = true;
        });
      });
      // @ts-ignore
    } else if (!google.client && window.gapi) {
      // @ts-ignore
      window.gapi.load('client', () => {
        // @ts-ignore
        google.client = window.gapi.client;
        googleApiLoaded = true;
      });
      // @ts-ignore
    } else if (!google.auth && window.gapi) {
      // @ts-ignore
      window.gapi.load('auth', () => {
        // @ts-ignore
        google.auth = window.gapi.auth;
        googleApiLoaded = true;
      });
    }

    /**
   * Opens a prompt for user to resign in if user wants to convert page to PDF.
   * @returns {*|modal}
   */
    const googleResignInModalOption = {
      fontAwesomeCode: 'fab fa-google-drive',
      titleCode: 'snapshot_resigin_modal_heading',
      bodyCode: 'snapshot_resigin_modal_content',
      okButtonCode: 'snapshot_resigin_modal_signin',
      cancelButtonCode: 'snapshot_modal_not_now',
      showCancelButton: true,
      windowClass: 'updated-modal-design bulb-snapshot-pdf',
      focusAfterClosed: undefined,
    };

    /**
   * Opens a prompt for user to decide if he wants to replace the embedBlock with pdf.
   * @returns {*|modal}
   */
    const googleDocDeletedModalOption = {
      fontAwesomeCode: 'fab fa-google-drive',
      titleCode: 'snapshot_resigin_modal_heading',
      bodyCode: 'snapshot_file_deleted_modal_content',
      okButtonCode: 'snapshot_file_deleted_modal_yes',
      cancelButtonCode: 'snapshot_modal_not_now',
      showCancelButton: true,
      windowClass: 'updated-modal-design bulb-snapshot-pdf',
    };

    /**
   * Opens a modal for user to enable snapshotPdf feature in order to embed google docs with
   * restricted organization access.
   * @returns {*|modal}
   */
    const orgPermissionModalOptions = {
      fontAwesomeCode: 'fab fa-google-drive',
      titleCode: 'assetlibrary_googledoc_org_permission_heading',
      bodyCode: 'assetlibrary_googledoc_org_permission_subheading',
      okButtonCode: 'assetlibrary_googledoc_org_permission_enable',
      cancelButtonCode: 'assetlibrary_googledoc_org_permission_cancel',
      showCancelButton: true,
      windowClass: 'updated-modal-design bulb-aero-modal',
    };

    /**
   * Get the google user from google userinfo API.
   * @returns {promise}
   */
    function getGoogleUser() {
      const deferred = $q.defer();
      if (google.client) {
        google.client.request({
          path: SNAPSHOTPDF.USER_INFO_URL,
        }).then((googleUser) => {
          googleId = googleUser.result.id;
          deferred.resolve();
        }, (error) => {
          deferred.reject(error);
        });
      }
      return deferred.promise;
    }

    /**
   * Handle response from google api during authorization process.
   * @param authResult result sent by google api.
   * @returns {*|promise}
   */
    const handleAuthResult = function (authResult) {
      const deferred = $q.defer();
      if (authResult && !authResult.error) {
        /* jshint camelcase: false */
        that.oauthCode = authResult.code;
        getGoogleUser().then(() => {
          deferred.resolve();
        });
      } else {
        deferred.reject();
      }
      return deferred.promise;
    };

    /**
   *  Initializes the Google API client library and sets up sign-in state
   *  listeners.
   */
    function initClient() {
      const deferred = $q.defer();
      if (googleApiLoaded) {
        that.oauthCode = null;
        google.auth.signOut();
        google.auth.authorize({
          client_id: CONFIG.google.client.id,
          scope: SCOPES,
          response_type: 'code token',
          prompt: 'consent',
        }, (authResult) => {
          handleAuthResult(authResult).then(deferred.resolve);
        });
      } else {
        BulbErrorService.showError($translate.instant('google_drive_api_not_loaded'));
      }
      return deferred.promise;
    }

    /**
   * Check if the GoogleOauth already exists.
   * @param code Google oauth code.
   */
    function isExistingGoogleOauthUser(code) {
      const deferred = $q.defer();
      getGoogleUser().then((googleUser) => {
        // Verify google connection exist with other account or not .
        Restangular.one('accounts/verifyGoogleConnection/GOOGLE_OAUTH_TOKEN')
          .get({ code }).then((response) => {
            deferred.resolve(response);
          }).catch((error) => {
            that.showError(error);
            deferred.reject(error);
          });
      });
      return deferred.promise;
    }

    /**
   * Show the google signin prompt.
   */
    function doGoogleLogin() {
      const deferred = $q.defer();
      initClient().then((auth) => {
        importExistingPdf(deferred).then(() => {
          BulbAuthService.fetchCurrentUser();
          deferred.resolve();
        });
      });
    }

    /**
   * Convert all existing google asset into snapshot pdf.
   * @param {promise}deferred
   */
    let importExistingPdf = function (deferred) {
      BulbBusyCoverService.showBusyDuring(
        snapshotPdf.all('importAll').post('', {
          code: that.oauthCode,
          googleId,
        }).then((response) => {
          that.oauthCode = null;
          deferred.resolve(response);
        }).catch((error) => {
          that.showError(error);
          deferred.reject(error);
        }),
      );
      return deferred.promise;
    };

    /**
   * Update the refresh token in Db if the token has been expired or access has been revoked.
   * @param {promise}deferred
   */
    const updateRefreshToken = function (deferred) {
      BulbBusyCoverService.showBusyDuring(
        snapshotPdf.all('updateRefreshToken').post('', {
          code: that.oauthCode,
          googleId,
        }).then((response) => {
          that.oauthCode = null;
          deferred.resolve(response);
        }).catch((error) => {
          that.showError(error);
          deferred.reject(error);
        }),
      );
      return deferred.promise;
    };

    /**
   * Convert imported google asset into snapshot pdf .
   * @param id Id of google asset.
   * @param code OAuth code of google signin.
   * @param contentBlockId Id of content block.
   * @param unitId Id of unit.
   */
    const importNewSnapshotPdf = function (id, code, contentBlockId, unitId) {
      const deferred = $q.defer();

      if (googleId) {
        BulbBusyCoverService.showBusyDuring(
          snapshotPdf.all('import').post('', {
            code,
            fileId: id,
            googleId,
            contentBlockId,
            unitId,
          }).then((response) => {
            that.oauthCode = null;
            deferred.resolve(response);
          }).catch((error) => {
            deferred.reject(error);
            if (error.status === 401 && !isSwitchedUser()) {
              BulbModalService.show(googleResignInModalOption).then(() => {
                initClient().then((auth) => {
                  importNewSnapshotPdf(id, that.oauthCode, contentBlockId, unitId).then(deferred.resolve);
                });
              });
            } else {
              that.showError(error);
            }
          }),
        );
      } else {
        importNewSnapshotPdfWithPrompt(id, unitId).then((response) => {
          deferred.resolve(response);
        });
      }
      return deferred.promise;
    };

    /**
   * Import google doc as pdf for organization restricted docs.
   * @param id of the google document
   * @param unitId of the unit in which doc is to be embedded
   * @param code google auth code for the user
   */
    this.importPdfForOrgRestrictedDoc = function (id, unitId, code) {
      const deferred = $q.defer();
      if (BulbAuthService.currentUser.prefs.importingGoogleAssetAsPdfEnabled) {
        if (BulbAuthService.currentUser.connections.googleOAuthToken) {
          isExistingGoogleOauthUser(code).then((status) => {
            if (status === true) {
              importNewSnapshotPdf(id, null, null, unitId).then((response) => {
                deferred.resolve(response);
              });
            } else {
              importNewSnapshotPdfWithPrompt(id, unitId).then((response) => {
                deferred.resolve(response);
              });
            }
          });
        } else {
          importNewSnapshotPdfWithPrompt(id, unitId).then((response) => {
            deferred.resolve(response);
          });
        }
      } else {
        BulbModalService.show(orgPermissionModalOptions).then(() => {
          patchAccountPrefs();
          importNewSnapshotPdfWithPrompt(id, unitId).then((response) => {
            deferred.resolve(response);
          });
        });
      }
      return deferred.promise;
    };

    /**
   * After google asset attached on page then convert into snapshot pdf.
   */
    this.importPdfFromGoogleDrive = function (id, code, contentBlockId, unitId) {
      const deferred = $q.defer();

      if (BulbAuthService.currentUser.connections.googleOAuthToken) {
        if (!code) {
          importNewSnapshotPdf(id, null, contentBlockId, unitId).then(deferred.resolve);
        } else if (!isSwitchedUser()) {
          isExistingGoogleOauthUser(code).then((status) => {
            if (status === true) {
              importNewSnapshotPdf(id, null, contentBlockId, unitId).then(deferred.resolve);
            } else {
              BulbModalService.show(googleResignInModalOption).then(() => {
                patchAccountPrefs();
                initClient().then((auth) => {
                  importNewSnapshotPdf(id, that.oauthCode, contentBlockId, unitId).then(deferred.resolve);
                });
              });
            }
          });
        }
      }
      return deferred.promise;
    };

    /**
   * Show google sign-in prompt to get refresh token and import new snapshotPdf into bulb.
   * @param fileId of the google file
   * @param unitId of the unit in which doc has to be embedded
   */
    let importNewSnapshotPdfWithPrompt = function (fileId, unitId) {
      const deferred = $q.defer();
      initClient().then(() => {
        importNewSnapshotPdf(fileId, that.oauthCode, null, unitId).then((response) => {
          deferred.resolve(response);
        });
      });
      return deferred.promise;
    };

    /**
   * Call api to update existing snapshot pdf of current unit.
   * @param unitId Id of unit.
   * @param code Google oauth code.
   * @param googleId Id of google account.
   */
    function update(unitId, code, googleId) {
      const deferred = $q.defer();
      BulbBusyCoverService.showBusyDuring(
        snapshotPdf.all('update').patch('', {
          code,
          googleId,
          unitId,
        }).then((response) => {
          that.oauthCode = null;
          deferred.resolve(response);
        }).catch((error) => {
          if (error.status !== 401) {
            that.showError(error);
          }
          deferred.reject(error);
        }),
      );
      return deferred.promise;
    }

    /**
   * Update snapshot pdf for current unit if page has google asset.
   * @param {object} unit.
   */
    this.updateSnapshotPdf = function (unit) {
      if (unit.contentBlocks) {
        let isExist = false;
        for (const key in unit.contentBlocks) {
          isExist = unit.contentBlocks[key].hasOwnProperty('snapshotPdfId')
          || (unit.contentBlocks[key].hasOwnProperty('html')
            && unit.contentBlocks[key].html.includes('docs.google.com'));
          if (isExist) {
            break;
          }
        }
        if (!isExist) {
          return false;
        }
      } else {
        return false;
      }

      const deferred = $q.defer();
      replaceEmbedWithAttachedFile(unit).then(() => {
        update(unit.id, null, null).catch((error) => {
          if (error.status === 401 && !isSwitchedUser()) {
            BulbModalService.show(googleResignInModalOption).then(() => {
              initClient().then((auth) => {
                updateRefreshToken(deferred).then(() => {
                  update(unit.id, that.oauthCode, googleId).then(deferred.resolve);
                });
              });
            });
          }
          deferred.reject(error);
        });
      });

      return deferred.promise;
    };

    /**
   * Replace deleted doc embeds with attached file embed blocks.
   * @param unit for which we are replacing the embedBlock
   * @returns {*|promise}
   */
    let replaceEmbedWithAttachedFile = function (unit) {
      const deferred = $q.defer();
      if (unit.deletedGoogleDocEmbedIds && unit.deletedGoogleDocEmbedIds.length > 0) {
        BulbModalService.show(googleDocDeletedModalOption).then(() => {
          // call new api here
          BulbBusyCoverService.showBusyDuring(
            snapshotPdf.all('replace').patch('', {
              deletedGoogleDocEmbedIds: unit.deletedGoogleDocEmbedIds,
              unitId: unit.id,
            }).then((response) => {
              BulbAjaxService.getUnit(unit.id, false).then((data) => {
                BulbUnitService.setCurrentUnit(data.unitDTOs[0]);
                BulbPageService.updatePageAfterReplaceEmbed();
                deferred.resolve();
              });
            }).catch((error) => {
              deferred.resolve();
            }),
          );
        });
      } else {
        deferred.resolve();
      }
      return deferred.promise;
    };

    /**
   * Patch the account prefs if the user enables the snapshot functionality.
   */
    let patchAccountPrefs = function () {
      const patch = { prefs: that.currentUser.prefs };
      if (typeof that.currentUser.prefs !== 'undefined' && !that.currentUser.prefs.importingGoogleAssetAsPdfEnabled) {
        patch.prefs.importingGoogleAssetAsPdfEnabled = true;
      }
      if (Object.keys(patch).length > 0) {
        sendAccountUpdate(patch);
      }
    };

    /**
   * Update pref of snapshot pdf.
   * @param patch Updated object.
   */
    let sendAccountUpdate = function (patch) {
      BulbBusyService.showBusyDuring(
        Restangular.one('accounts', that.currentUser.id).patch(patch).then(() => {
        }, (error) => {
          BulbErrorService.showError(null, error);
        }),
      );
    };

    /**
   * Show error if google account already connected with another account.
   * @param error object of error.
   */
    this.showError = function (error) {
      if (error.status === 409 && error.data && error.data.developerMessage) {
        // If we have detected an account conflict, show the information of the conflicting account:
        const parts = error.data.developerMessage.split(',');
        const params = {
          email: `<strong>${parts[0]}</strong>`,
        };
        const url = `${CONFIG.base.url}/${parts[1]}`;
        BulbErrorService.showError(`${$translate.instant('account_already_exists_error', params)
        }<a href=\"${url}\" target=\"_blank\">${url}</a>`, error);
      } else {
        BulbErrorService.showError(null, error);
      }
    };

    /**
   * Return true if user has role SWITCHED_USER.
   */
    let isSwitchedUser = function () {
      return BulbAuthService.currentUser.hasRole(AUTH.ROLES.SWITCHED_USER);
    };
  }
}());
